import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useRetailer } from "@/hooks/use-retailer";
import { zodResolver } from "@hookform/resolvers/zod";
import { Globe, Loader2, Upload } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

const formSchema = z.object({
	name: z.string().min(2, {
		message: "Name must be at least 2 characters.",
	}),
	website: z
		.string()
		.url({
			message: "Please enter a valid URL.",
		})
		.optional()
		.or(z.literal("")),
});

export function RetailerProfileSection() {
	const { user } = useAuth();
	const {
		retailerProfile,
		isLoading,
		updateProfile,
		uploadProfileImage,
		isUpdating,
	} = useRetailer();
	const [uploadingImage, setUploadingImage] = useState(false);

	// Initialize form with react-hook-form
	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			name: "",
			website: "",
		},
	});

	// Update form values when profile data is loaded
	useEffect(() => {
		if (retailerProfile) {
			form.reset({
				name: retailerProfile.name || "",
				website: retailerProfile.website || "",
			});
		}
	}, [retailerProfile, form]);

	const handleSubmit = (values: z.infer<typeof formSchema>) => {
		updateProfile({
			name: values.name,
			website: values.website || undefined,
		});
	};

	const handleImageUpload = async (
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		const file = e.target.files?.[0];
		if (!file || !user) return;

		if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
			form.setError("root", {
				message: "Please upload a JPEG, PNG, or WebP image.",
			});
			return;
		}

		if (file.size > 5 * 1024 * 1024) {
			form.setError("root", {
				message: "Please upload an image smaller than 5MB.",
			});
			return;
		}

		try {
			setUploadingImage(true);
			await uploadProfileImage(file);
		} finally {
			setUploadingImage(false);
		}
	};

	if (isLoading) {
		return (
			<div className="space-y-6">
				<div className="h-8 w-64 bg-gray-200 animate-pulse rounded"></div>
				<div className="h-32 bg-gray-200 animate-pulse rounded"></div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div>
				<h2 className="text-2xl font-semibold tracking-tight">
					Retailer Profile
				</h2>
				<p className="text-gray-500">
					Update your retailer information to help suppliers
					understand your business.
				</p>
			</div>

			<div className="bg-white border border-gray-100 rounded-lg shadow-sm">
				<div className="p-6">
					<div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center mb-6">
						<div className="relative">
							<Avatar className="h-24 w-24">
								<AvatarImage
									src={
										retailerProfile?.profile_image ||
										undefined
									}
									alt={retailerProfile?.name || ""}
								/>
								<AvatarFallback className="text-2xl bg-primary/10 text-primary">
									{(retailerProfile?.name || "")
										.split(" ")
										.map((n) => n[0])
										.join("")
										.toUpperCase()
										.slice(0, 2)}
								</AvatarFallback>
							</Avatar>

							<label
								htmlFor="profile-image"
								className="absolute -bottom-2 -right-2 bg-accent text-accent-foreground p-1.5 rounded-full cursor-pointer hover:bg-accent/90 transition-colors">
								{uploadingImage ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									<Upload className="h-4 w-4" />
								)}
							</label>
							<input
								id="profile-image"
								type="file"
								accept="image/jpeg, image/png, image/webp"
								className="hidden"
								onChange={handleImageUpload}
								disabled={uploadingImage || isUpdating}
							/>
						</div>

						<div>
							<h3 className="text-lg font-medium">
								{retailerProfile?.name ||
									"Your Retail Business"}
							</h3>
							{retailerProfile?.website && (
								<a
									href={
										retailerProfile.website.startsWith(
											"http"
										)
											? retailerProfile.website
											: `https://${retailerProfile.website}`
									}
									target="_blank"
									rel="noopener noreferrer"
									className="text-gray-500 text-sm flex items-center gap-1 hover:text-primary transition-colors">
									<Globe className="h-3.5 w-3.5" />
									{retailerProfile.website}
								</a>
							)}
						</div>
					</div>

					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(handleSubmit)}
							className="space-y-4">
							{form.formState.errors.root && (
								<div className="text-sm font-medium text-destructive">
									{form.formState.errors.root.message}
								</div>
							)}

							<FormField
								control={form.control}
								name="name"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Business Name</FormLabel>
										<FormControl>
											<Input
												{...field}
												placeholder="Your Retail Business"
											/>
										</FormControl>
										<FormDescription>
											This name will be visible to
											suppliers.
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="website"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Website</FormLabel>
										<FormControl>
											<Input
												{...field}
												placeholder="https://yourwebsite.com"
											/>
										</FormControl>
										<FormDescription>
											Your business website (optional).
										</FormDescription>
										<FormMessage />
									</FormItem>
								)}
							/>

							<div className="mt-6">
								<Button
									type="submit"
									variant="accent"
									disabled={isUpdating || uploadingImage}>
									{isUpdating ? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											Saving...
										</>
									) : (
										"Save Changes"
									)}
								</Button>
							</div>
						</form>
					</Form>
				</div>
			</div>
		</div>
	);
}
